import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import './ProfitAndLoss.css';

const FINANCE_NAME = 'M/s KALABHAIRAV FINANCE AND LEASING (R)';
const FINANCE_ADDRESS = 'Khajuri, Aland , Kalaburgi';

const DEFAULT_YEAR_FROM = '2025-04-01';
const DEFAULT_YEAR_TO = '2026-03-31';

const EXPENSE_HEADER_ORDER = [
  'Office Expenditure A/C',
  'POOJA A/C',
  'PRITING & STATIONRY A/C',
  'FURNITURE A/C',
  'TELE PHONE A/C',
  'ROOM RENT A/C',
  'BULDING DEP A/C',
  'MISC A/C',
  'LEGAL ADVISOR & ADIT F',
  'MEETING EXP A/C',
  'SECURITY DEP A/C',
  'NSC',
  ' LICENCE FEES A/C',
  'LICENCE FEES A/C',
  'COMPUTER A/C',
  'STAMP DUTY A/C',
  'ELECTRICAL EXP A/C',
  'NEWS PAPER & CABLE A/C',
  'Interest On Capital',
  'M.G.PARTNER SALARI A/C',
  'M.G.PARTNER SALARY A/C',
  'PARTNER SALARIES A/C',
  'STAFF SALARY A/C',
  'Intrest Paid On Deposit A/c',
  'NETT LOSS A/C',
  'Professional Tax',
  'Income Tax',
  'BANK CHARGES',
  'Document Charges A/C',
  'CC TV',
  'ACCOUNT SOFTWARE',
  'MD SALARY',
  'BATTA',
  'SOFTWARE A/C',
  'GOLD PURCHASE',
  'PARTNERS INCENTIVE',
  'Tea Expenditure',
  'B Expenditure',
  'Donation',
  'IT Return File',
];

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const PF_PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 14mm 10mm 16mm;
  }
  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    background: #fff !important;
    color: #000;
    font-family: 'Times New Roman', Times, serif;
  }
  .pf-print-statement {
    width: 100%;
    background: #fff;
  }
  .pf-header {
    text-align: center;
    margin-bottom: 18px;
    padding-bottom: 12px;
    border-bottom: 1px solid #333;
  }
  .pf-header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11pt;
    margin-bottom: 8px;
  }
  .pf-page-info {
    font-family: 'Courier New', Courier, monospace;
    font-size: 10pt;
  }
  .pf-company-name {
    margin: 0;
    color: #8b0000;
    font-size: 18pt;
    font-weight: 800;
    line-height: 1.25;
  }
  .pf-company-address {
    margin: 5px 0 10px;
    font-size: 11pt;
  }
  .pf-report-title {
    margin: 0;
    font-size: 12pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .pf-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11pt;
    background: #fff;
  }
  .pf-table th,
  .pf-table td {
    border: 1px solid #222;
    padding: 8px 10px;
    vertical-align: top;
    background: #fff;
  }
  .pf-side-heading {
    text-align: center;
    font-weight: 700;
    text-transform: uppercase;
    background: #fff;
  }
  .pf-amount-col {
    width: 140px;
    text-align: right;
    white-space: nowrap;
    font-family: 'Courier New', Courier, monospace;
  }
  .pf-total-row td {
    font-weight: 700;
    border-top: 2px solid #222;
  }
`;

const ProfitAndLoss = () => {
  const [dateFrom, setDateFrom] = useState(DEFAULT_YEAR_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_YEAR_TO);
  const [balanceData, setBalanceData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select both from and to dates.');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('From date cannot be greater than To date.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [balanceResponse, expenseResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/balance-enquiry?date_from=${dateFrom}&date_to=${dateTo}`),
        fetch(`${API_BASE_URL}/expense-manager`),
      ]);

      if (!balanceResponse.ok) {
        const payload = await balanceResponse.json().catch(() => ({}));
        throw new Error(payload.message || 'Failed to fetch balance enquiry data.');
      }
      if (!expenseResponse.ok) {
        const payload = await expenseResponse.json().catch(() => ({}));
        throw new Error(payload.message || 'Failed to fetch expense data.');
      }

      const balanceJson = await balanceResponse.json();
      const expenseJson = await expenseResponse.json();

      setBalanceData(balanceJson);
      setExpenses(expenseJson.expenses || []);
    } catch (err) {
      console.error('P&F fetch failed:', err);
      setError(err.message || 'Unable to load profit and loss data.');
      setBalanceData(null);
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const expenditureRows = useMemo(() => {
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    const totalsByHeader = {};

    expenses.forEach((expense) => {
      const expenseDate = new Date(expense.date);
      if (expenseDate < fromDate || expenseDate > toDate) return;

      const header = String(expense.headerName || '').trim();
      if (!header) return;

      totalsByHeader[header] = (totalsByHeader[header] || 0) + Number(expense.amount || 0);
    });

    const knownHeaders = EXPENSE_HEADER_ORDER.filter((header) => totalsByHeader[header] > 0);
    const extraHeaders = Object.keys(totalsByHeader)
      .filter((header) => !EXPENSE_HEADER_ORDER.includes(header))
      .sort((a, b) => a.localeCompare(b));

    return [...knownHeaders, ...extraHeaders].map((header) => ({
      label: `To ${header} Dr`,
      amount: totalsByHeader[header],
    }));
  }, [expenses, dateFrom, dateTo]);

  const glInterest = Number(balanceData?.generalLoanInterest || 0);
  const cdInterest = Number(balanceData?.cdLoanInterest || 0);
  const documentCharges = Number(balanceData?.documentChargesCredit || 0);
  const totalIncome = glInterest + cdInterest + documentCharges;
  const totalExpenditure = expenditureRows.reduce((sum, row) => sum + row.amount, 0);

  const netIncome = totalIncome > totalExpenditure ? totalIncome - totalExpenditure : 0;
  const netLoss = totalExpenditure > totalIncome ? totalExpenditure - totalIncome : 0;
  const leftTotal = totalExpenditure + netIncome;
  const rightTotal = totalIncome + netLoss;

  const periodHeading = `PROFIT AND LOSS ACCOUNT FOR THE YEAR ${formatDateToDDMMYYYY(dateFrom)} To ${formatDateToDDMMYYYY(dateTo)}`;

  const leftRows = useMemo(() => {
    const rows = expenditureRows.map((row) => ({
      label: row.label,
      amount: formatAmount(row.amount),
    }));
    if (netIncome > 0) {
      rows.push({ label: 'Net Income', amount: formatAmount(netIncome) });
    }
    return rows;
  }, [expenditureRows, netIncome]);

  const rightRows = useMemo(() => {
    const rows = [
      { label: 'By GL interest A/c Cr', amount: formatAmount(glInterest) },
      { label: 'By Cd interest A/C Cr', amount: formatAmount(cdInterest) },
      { label: 'By Document Charges A/C Cr', amount: formatAmount(documentCharges) },
    ];
    if (netLoss > 0) {
      rows.push({ label: 'Net Loss', amount: formatAmount(netLoss) });
    }
    return rows;
  }, [glInterest, cdInterest, documentCharges, netLoss]);

  const bodyRowCount = Math.max(leftRows.length, rightRows.length);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Please allow popups to print the profit and loss statement.');
      return;
    }

    const tableRows = Array.from({ length: bodyRowCount })
      .map((_, index) => {
        const left = leftRows[index];
        const right = rightRows[index];
        return `
          <tr>
            <td>${escapeHtml(left?.label || '')}</td>
            <td class="pf-amount-col">${escapeHtml(left?.amount || '')}</td>
            <td>${escapeHtml(right?.label || '')}</td>
            <td class="pf-amount-col">${escapeHtml(right?.amount || '')}</td>
          </tr>
        `;
      })
      .join('');

    const printHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Profit and Loss - ${escapeHtml(formatDateToDDMMYYYY(dateFrom))} to ${escapeHtml(formatDateToDDMMYYYY(dateTo))}</title>
          <style>${PF_PRINT_STYLES}</style>
        </head>
        <body>
          <div class="pf-print-statement">
            <div class="pf-header">
              <div class="pf-header-top">
                <span></span>
                <span class="pf-page-info">Page 1 of 1</span>
              </div>
              <h1 class="pf-company-name">${escapeHtml(FINANCE_NAME)}</h1>
              <p class="pf-company-address">${escapeHtml(FINANCE_ADDRESS)}</p>
              <h2 class="pf-report-title">${escapeHtml(periodHeading)}</h2>
            </div>
            <table class="pf-table">
              <thead>
                <tr>
                  <th colspan="2" class="pf-side-heading">Expenditure</th>
                  <th colspan="2" class="pf-side-heading">Income</th>
                </tr>
                <tr>
                  <th>Particulars</th>
                  <th class="pf-amount-col">Amount</th>
                  <th>Particulars</th>
                  <th class="pf-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
                <tr class="pf-total-row">
                  <td><strong>Total</strong></td>
                  <td class="pf-amount-col"><strong>${escapeHtml(formatAmount(leftTotal))}</strong></td>
                  <td><strong>Total</strong></td>
                  <td class="pf-amount-col"><strong>${escapeHtml(formatAmount(rightTotal))}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          <\/script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <div className="pf-container">
      <div className="pf-toolbar no-print">
        <div className="pf-toolbar-title">
          <h2>P&amp;F</h2>
          <p>Profit and Loss account</p>
        </div>
        <div className="pf-toolbar-actions">
          <div className="pf-filter">
            <div className="filter-group">
              <label htmlFor="pf-from">From</label>
              <input
                id="pf-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="pf-to">To</label>
              <input
                id="pf-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <button type="button" className="pf-btn pf-btn-secondary" onClick={fetchData} disabled={isLoading}>
              Refresh
            </button>
            <button type="button" className="pf-btn pf-btn-primary" onClick={handlePrint} disabled={isLoading || !!error}>
              Print
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="pf-error no-print">{error}</div> : null}
      {isLoading ? <div className="pf-loading">Loading profit and loss data…</div> : null}

      {!isLoading && !error ? (
        <div className="pf-statement">
          <div className="pf-header">
            <div className="pf-header-top">
              <span />
              <span className="pf-page-info">Page 1 of 1</span>
            </div>
            <h1 className="pf-company-name">{FINANCE_NAME}</h1>
            <p className="pf-company-address">{FINANCE_ADDRESS}</p>
            <h2 className="pf-report-title">{periodHeading}</h2>
          </div>

          <div className="pf-table-wrapper">
            <table className="pf-table">
              <thead>
                <tr>
                  <th colSpan="2" className="pf-side-heading">
                    Expenditure
                  </th>
                  <th colSpan="2" className="pf-side-heading">
                    Income
                  </th>
                </tr>
                <tr>
                  <th>Particulars</th>
                  <th className="pf-amount-col">Amount</th>
                  <th>Particulars</th>
                  <th className="pf-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: bodyRowCount }).map((_, index) => {
                  const left = leftRows[index];
                  const right = rightRows[index];
                  return (
                    <tr key={`row-${index}`}>
                      <td>{left?.label || ''}</td>
                      <td className="pf-amount-col">{left?.amount || ''}</td>
                      <td>{right?.label || ''}</td>
                      <td className="pf-amount-col">{right?.amount || ''}</td>
                    </tr>
                  );
                })}
                <tr className="pf-total-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="pf-amount-col">
                    <strong>{formatAmount(leftTotal)}</strong>
                  </td>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="pf-amount-col">
                    <strong>{formatAmount(rightTotal)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      ) : null}
    </div>
  );
};

export default ProfitAndLoss;
